import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { ContractReference } from 'Cube/domain/cube.domain.types'
import { usePhasePermissions } from 'Cube/utils/usePhasePermissions'
import invariant from 'tiny-invariant'
import {
  getAddableContractReferenceTypes,
  getVisibleContractReferences
} from './contractReferences.helpers'

/**
 * Reads and edits one phase's contract references. The phase header badges,
 * the phase menu and the integration tabs all go through this, so they edit
 * the same list.
 */
export const usePhaseContractReferences = (props: { phaseId: string }) => {
  const cubeCtx = useCubeContext()
  const { canUpdatePhase } = usePhasePermissions()

  const resolvedPhase = cubeCtx.queries.resolvedPhases[props.phaseId]

  invariant(resolvedPhase, `Phase with id ${props.phaseId} not found`)

  const contractReferencesFeature =
    cubeCtx.queries.availableFeatures.phases[resolvedPhase.id].phase
      .contractReferences
  // While references are on, this flag only says whether NetSuite is connected.
  const isNetsuiteAvailable =
    cubeCtx.queries.rawData.configuration.features.contractTagEditing
  const customerId = cubeCtx.queries.rawData.data.common.customerId
  const allReferences = resolvedPhase.contractReferences

  /**
   * Any edit means the next save has to send every phase's full list, so the
   * marker goes in with the same update.
   */
  const setReferences = (contractReferences: Array<ContractReference>) => {
    cubeCtx.mutators.updateData({
      phases: {
        [props.phaseId]: {
          contractReferences
        }
      },
      schedule: {
        replaceContractReferences: true
      }
    })
  }

  return {
    isVisible: contractReferencesFeature.available.visible,
    canEdit: contractReferencesFeature.available.enabled && canUpdatePhase,
    customerId,
    allReferences,
    references: getVisibleContractReferences({
      references: allReferences,
      isNetsuiteAvailable
    }),
    addableTypes: getAddableContractReferenceTypes({
      references: allReferences,
      isNetsuiteAvailable,
      hasCustomer: Boolean(customerId)
    }),
    addReference: (reference: ContractReference) =>
      setReferences([...allReferences, reference]),
    updateReference: (index: number, reference: ContractReference) =>
      setReferences(
        allReferences.map((existing, existingIndex) =>
          existingIndex === index ? reference : existing
        )
      ),
    removeReference: (index: number) =>
      setReferences(
        allReferences.filter((_, existingIndex) => existingIndex !== index)
      )
  }
}
